import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import PageHero from "@/components/feature/PageHero";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EvidenceCard from "@/components/feature/EvidenceCard";
import { evidenceItems } from "@/mocks/evidence";

const categoryOrder = ["全部", "时间", "地点", "身份", "细节", "来源", "技术"];

export default function EvidencePage() {
  const [active, setActive] = useState("全部");

  const categories = useMemo(() => {
    const present = new Set(evidenceItems.map((item) => item.category));
    return categoryOrder.filter((c) => c === "全部" || present.has(c));
  }, []);

  const list = useMemo(
    () =>
      active === "全部"
        ? evidenceItems
        : evidenceItems.filter((item) => item.category === active),
    [active],
  );

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "511爆料证据对照室｜来源求实与细节补充",
      description:
        "把关于 511 爆料 的竞争性说法并排对照，标注双方来源等级、判定结果与可信度评估。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://balha.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "证据对照室",
          item: "https://balha.cn/evidence",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "511爆料证据对照条目",
      itemListElement: evidenceItems.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.claim,
      })),
    },
  ];

  return (
    <SiteLayout>
      <PageMeta
        title="511爆料证据对照室｜来源求实与511爆料细节补充"
        description="511爆料证据对照室，把竞争性说法并排对照，标注双方来源等级、判定结果与可信度评估，让来源求实与细节补充都有可复核的依据。"
        keywords="511爆料来源求实,511爆料补充证据,511爆料细节补充,证据对照,可信度评估"
        path="/evidence"
        schema={schema}
      />

      <PageHero
        eyebrow="EVIDENCE ROOM"
        title="证据对照室：让材料并排，而不是替你先下判断"
        description="每一行只处理一个说法。左边列出支持与反对的来源与等级，右边给出判定和可信度，读者可以照着材料自己再走一遍。"
        crumbs={
          <Breadcrumbs
            items={[{ label: "首页", to: "/" }, { label: "证据对照室" }]}
          />
        }
      />

      <section className="w-full px-4 py-10 md:px-6 md:py-14 lg:px-10">
        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setActive(category)}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                active === category
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300 hover:text-foreground-950"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 gap-5">
              {list.map((item) => (
                <EvidenceCard key={item.id} item={item} />
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-accent-300 bg-accent-100 p-5">
              <h2 className="font-heading text-base font-semibold text-accent-900">
                怎么读这张对照表
              </h2>
              <ol className="mt-4 flex flex-col gap-3 text-xs leading-relaxed text-accent-900/90">
                <li className="flex gap-2">
                  <span className="font-heading font-bold">1.</span>
                  先看「待验证说法」是否具体，越具体的说法越容易判定。
                </li>
                <li className="flex gap-2">
                  <span className="font-heading font-bold">2.</span>
                  再看两份来源的等级。等级相同但同源时，不构成独立印证。
                </li>
                <li className="flex gap-2">
                  <span className="font-heading font-bold">3.</span>
                  最后看可信度进度条与对照结论，理解它为什么得到这个分数。
                </li>
              </ol>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                常见判定含义
              </h2>
              <ul className="mt-4 flex flex-col gap-3 text-xs leading-relaxed text-foreground-600">
                <li>
                  <span className="font-medium text-foreground-900">一致：</span>
                  两份独立材料在关键细节上互相吻合。
                </li>
                <li>
                  <span className="font-medium text-foreground-900">部分推翻：</span>
                  材料推翻了说法的一部分，其余仍待核实。
                </li>
                <li>
                  <span className="font-medium text-foreground-900">存在矛盾：</span>
                  两组说法无法同时成立，双方都缺一手材料。
                </li>
                <li>
                  <span className="font-medium text-foreground-900">已排除 / 已推翻：</span>
                  有可核验材料证明原说法不成立，条目会保留更正说明。
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}